import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { formatDate } from '../../utils/dateUtils';
import { formatPlanPrice } from '../../utils/planPrice';
import ReportsTable from './ReportsTable';

const roles = ['user', 'special', 'staff', 'admin'];
const packages = ['free', 'pro', 'advanced'];

function toDateInputValue(value) {
  if (!value) return '';
  return new Date(value).toISOString().slice(0, 10);
}

function initials(username) {
  return username ? username.slice(0, 2).toUpperCase() : '??';
}

function AccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { username: currentUsername, role, adminUpdateAccount, adminDeleteAccount } = useAuth();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const { fetchUserSubscription } = useSubscription();
  const { t } = useTranslation('admin');
  const { t: tSub } = useTranslation('subscription');

  const canManage = role === 'admin';
  const backTo = role === 'staff' ? '/staff' : '/admin';
  const backLabel = role === 'staff' ? t('accountDetail.backToUsers') : t('accountDetail.backToAccounts');

  const [account, setAccount] = useState(null);
  const [status, setStatus] = useState('loading');
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportsStatus, setReportsStatus] = useState('loading');
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    api.get(`/users/${id}`)
      .then(({ user }) => {
        if (cancelled) return;
        setAccount(user);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    fetchUserSubscription(id)
      .then((data) => {
        if (!cancelled) setSubscriptionInfo(data);
      })
      .catch(() => {
        if (!cancelled) setSubscriptionInfo({ subscription: null, payments: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [id, fetchUserSubscription]);

  useEffect(() => {
    let cancelled = false;
    setReportsStatus('loading');
    api.get(`/reports/user/${id}`)
      .then(({ reports: history }) => {
        if (cancelled) return;
        setReports(history);
        setReportsStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setReportsStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleToggleResolve = async (report, nextStatus) => {
    try {
      const { report: updated } = await api.patch(`/reports/${report.id}`, { status: nextStatus });
      setReports((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      addToast(nextStatus === 'resolved' ? t('accountDetail.reportResolved') : t('accountDetail.reportReopened'), 'success');
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const startEditing = () => {
    setForm({
      username: account.username,
      email: account.email || '',
      password: '',
      role: account.role,
      subscriptionPackage: account.subscriptionPackage || 'free',
      subscriptionPurchasedAt: toDateInputValue(account.subscriptionPurchasedAt),
    });
    setError('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setForm(null);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const updates = {
      username: form.username,
      email: form.email,
      role: form.role,
      subscriptionPackage: form.subscriptionPackage,
      subscriptionPurchasedAt: form.subscriptionPurchasedAt || null,
    };
    if (form.password) updates.password = form.password;

    setIsSaving(true);
    const result = await adminUpdateAccount(id, updates);
    setIsSaving(false);

    if (!result.success) {
      setError(result.error);
      return;
    }

    const { user } = await api.get(`/users/${id}`);
    setAccount(user);
    setIsEditing(false);
    setForm(null);
    addToast(t('accountDetail.accountUpdated'), 'success');
  };

  const handleDelete = async () => {
    const confirmed = await confirm(t('accountDetail.confirmDelete', { username: account.username }));
    if (!confirmed) return;
    const result = await adminDeleteAccount(id);
    if (!result.success) {
      addToast(result.error, 'error');
      return;
    }
    addToast(t('accountDetail.accountDeleted'), 'success');
    navigate(backTo);
  };

  if (status === 'loading') {
    return (
      <section className="page-card">
        <Link to={backTo} className="text-btn back-link">{backLabel}</Link>
        <p className="chart-empty">{t('accountDetail.loadingAccount')}</p>
      </section>
    );
  }

  if (status === 'error' || !account) {
    return (
      <section className="page-card">
        <Link to={backTo} className="text-btn back-link">{backLabel}</Link>
        <p className="chart-empty">{t('accountDetail.accountNotFound')}</p>
      </section>
    );
  }

  return (
    <section className="page-card">
      <Link to={backTo} className="text-btn back-link">{backLabel}</Link>

      <div className="account-detail-header card">
        {account.avatarUrl ? (
          <img src={account.avatarUrl} alt="" className="profile-avatar profile-avatar-img profile-avatar-large" />
        ) : (
          <span className="profile-avatar profile-avatar-large">{initials(account.username)}</span>
        )}
        <div className="account-detail-heading">
          <div className="account-detail-title-row">
            <h2>{account.username}</h2>
            {account.username === currentUsername && <span className="account-you-badge">{t('you')}</span>}
          </div>
          <span className={`role-pill ${account.role}`}>{t(`roleLabels.${account.role}`)}</span>
        </div>
        {!isEditing && canManage && (
          <div className="account-detail-actions">
            <button type="button" className="text-btn" onClick={startEditing}>{t('accountDetail.edit')}</button>
            <button type="button" className="text-btn danger" onClick={handleDelete}>{t('accountDetail.delete')}</button>
          </div>
        )}
      </div>

      {!isEditing ? (
        <div className="card account-detail-grid">
          <div className="account-detail-field">
            <span className="account-detail-label">{t('accountDetail.email')}</span>
            <span className="account-detail-value">{account.email || t('dash')}</span>
          </div>
          <div className="account-detail-field">
            <span className="account-detail-label">{t('accountDetail.username')}</span>
            <span className="account-detail-value">{account.username}</span>
          </div>
          <div className="account-detail-field">
            <span className="account-detail-label">{t('accountDetail.role')}</span>
            <span className="account-detail-value"><span className={`role-pill ${account.role}`}>{t(`roleLabels.${account.role}`)}</span></span>
          </div>
          <div className="account-detail-field">
            <span className="account-detail-label">{t('accountDetail.joined')}</span>
            <span className="account-detail-value">{account.createdAt ? formatDate(account.createdAt) : t('dash')}</span>
          </div>
          <div className="account-detail-field">
            <span className="account-detail-label">{t('accountDetail.subscriptionPackage')}</span>
            <span className="account-detail-value"><span className={`package-pill ${account.subscriptionPackage || 'free'}`}>{t(`packageLabels.${account.subscriptionPackage || 'free'}`)}</span></span>
          </div>
          <div className="account-detail-field">
            <span className="account-detail-label">{t('accountDetail.subscriptionPurchased')}</span>
            <span className="account-detail-value">{account.subscriptionPurchasedAt ? formatDate(account.subscriptionPurchasedAt) : t('dash')}</span>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="card form-card account-detail-edit-form">
          <label>
            {t('accountDetail.username')}
            <input autoFocus value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} />
          </label>
          <label>
            {t('accountDetail.email')}
            <input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
          </label>
          <label>
            {t('accountDetail.newPasswordHint')}
            <input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} />
          </label>
          <label>
            {t('accountDetail.role')}
            <select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })}>
              {roles.map((role) => <option key={role} value={role}>{t(`roleLabels.${role}`)}</option>)}
            </select>
          </label>
          <label>
            {t('accountDetail.subscriptionPackage')}
            <select value={form.subscriptionPackage} onChange={(event) => setForm({ ...form, subscriptionPackage: event.target.value })}>
              {packages.map((pkg) => <option key={pkg} value={pkg}>{t(`packageLabels.${pkg}`)}</option>)}
            </select>
          </label>
          <label>
            {t('accountDetail.subscriptionPurchased')}
            <input type="date" value={form.subscriptionPurchasedAt} onChange={(event) => setForm({ ...form, subscriptionPurchasedAt: event.target.value })} />
          </label>
          {error && <span className="error-text">{error}</span>}
          <div className="modal-actions">
            <button type="button" className="text-btn" onClick={cancelEditing}>{t('accountDetail.cancel')}</button>
            <button className="primary-btn standout-btn" type="submit" disabled={isSaving}>{isSaving ? t('accountDetail.saving') : t('accountDetail.saveChanges')}</button>
          </div>
        </form>
      )}

      <div className="sheet-table-card">
        <div className="sheet-panel-header tint-mint"><span>{tSub('accountDetail.panelTitle')}</span></div>
        <div className="sheet-panel-body">
          {!subscriptionInfo?.subscription ? (
            <p className="chart-empty">{tSub('accountDetail.noSubscription')}</p>
          ) : (
            <>
              <div className="billing-summary-row">
                <span>{tSub('accountDetail.status')}</span>
                <strong>{subscriptionInfo.subscription.status}</strong>
              </div>
              <div className="billing-summary-row">
                <span>{tSub('accountDetail.lockedPrice')}</span>
                <strong>{formatPlanPrice(subscriptionInfo.subscription.lockedPrice, subscriptionInfo.subscription.currency)}</strong>
              </div>
              <div className="billing-summary-row">
                <span>{tSub('accountDetail.interval')}</span>
                <strong>{tSub(`interval.${subscriptionInfo.subscription.interval}Label`)}</strong>
              </div>
              <div className="billing-summary-row">
                <span>{tSub('accountDetail.nextRenewal')}</span>
                <strong>{new Date(subscriptionInfo.subscription.currentPeriodEnd).toLocaleDateString()}</strong>
              </div>
              {subscriptionInfo.subscription.canceledAt && (
                <div className="billing-summary-row">
                  <span>{tSub('accountDetail.canceledAt')}</span>
                  <strong>{new Date(subscriptionInfo.subscription.canceledAt).toLocaleDateString()}</strong>
                </div>
              )}
            </>
          )}

          <h4>{tSub('accountDetail.paymentHistory')}</h4>
          {(!subscriptionInfo?.payments || subscriptionInfo.payments.length === 0) ? (
            <p className="chart-empty">{tSub('accountDetail.noPayments')}</p>
          ) : (
            <div className="sheet-table-scroll">
              <table className="sheet-table">
                <thead>
                  <tr>
                    <th className="numeric">{tSub('admin.columnAmount')}</th>
                    <th>{tSub('admin.columnStatus')}</th>
                    <th>{tSub('admin.columnDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptionInfo.payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="numeric">{formatPlanPrice(payment.amount, payment.currency)}</td>
                      <td>{payment.status}</td>
                      <td>{new Date(payment.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ReportsTable
        title={t('accountDetail.reportHistory')}
        tint="rose"
        reports={reports}
        showUser={false}
        showResolveAction
        onToggleResolve={handleToggleResolve}
        emptyMessage={reportsStatus === 'loading' ? t('accountDetail.loadingReports') : t('accountDetail.noReportsFromUser')}
      />
    </section>
  );
}

export default AccountDetailPage;
